/**
 * Which answers the reader already rated this session (Task 251 Layer 2), so a
 * reload does not offer the buttons again. sessionStorage, like the chat's own
 * session persistence (Task 167): it clears with the tab and never leaves the
 * browser. Every access is guarded — private mode or blocked storage just means
 * the buttons come back after a reload, and the proxy ignores a second rating.
 */
import type { FeedbackRating } from "../api/feedback-client";

const STORAGE_KEY = "bow-and-arrow:feedback-rated";
const MAX_ENTRIES = 200;

function store(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.sessionStorage ?? null : null;
  } catch {
    return null;
  }
}

function readAll(): Record<string, FeedbackRating> {
  try {
    const raw = store()?.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return {};
    const out: Record<string, FeedbackRating> = {};
    for (const [id, rating] of Object.entries(parsed as Record<string, unknown>)) {
      if (rating === "useful" || rating === "not_useful") out[id] = rating;
    }
    return out;
  } catch {
    return {};
  }
}

/** The rating already sent for this answer in this session, if any. */
export function ratedThisSession(requestId: string): FeedbackRating | null {
  return readAll()[requestId] ?? null;
}

export function rememberRating(requestId: string, rating: FeedbackRating): void {
  try {
    const all = readAll();
    delete all[requestId];
    all[requestId] = rating;
    const ids = Object.keys(all);
    for (const id of ids.slice(0, Math.max(0, ids.length - MAX_ENTRIES))) delete all[id];
    store()?.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch {
    /* storage unavailable: nothing to remember */
  }
}
